import { createSelectors } from '@leverege/ui-redux';

// State is formed like this 
// {
//   resourceId : 
//     requestId, // id of the request data,
//     status : 'loading',
//     error : { },
//     limit : <number>,
//     count : <number>,
//     offset : <number>,
//     next : <token>
//     [namePlural] : [
//       { ...item0 }, { ...item1 }
//     ],
//     lookup : { <id> : <item> },
//     [name]Status : {
//       id : { status, error }
//     }
//     
// }
function create(options) {
  const {
    initialState = {},
    name,
    // name of an item. eg: user
    namePlural,
    // plural name of the items. eg: users
    idActionKey = 'id',
    // the key in the actions to get the item's unique id 
    idKey = 'id',
    // the key in the item to get the item's unique id 
    groupKey = 'resourceId',
    // the key used to get the unique id out of the action
    actionHandlers = null,
    // function( state, action, createState ). If this returns a different 
    // state object, the default reducers will be ignored
    countKey = 'count',
    limitKey = 'limit',
    offsetKey = 'offset',
    nextKey = 'next',
    itemsKey = 'items',
    itemKey = 'item',
    defaultStateKey,
    constantPrefix,
    merge = (nItem, oItem) => {
      return nItem;
    }
  } = options;
  if (constantPrefix == null) {
    throw new Error('constantPrefix needs to be defined');
  }
  const STATUS = `${constantPrefix}.status`;
  const ADD = `${constantPrefix}.add`;
  const UPDATE = `${constantPrefix}.update`;
  const REMOVE = `${constantPrefix}.remove`;
  const BULK_REMOVE = `${constantPrefix}.bulkRemove`;
  const LIST = `${constantPrefix}.list`;
  const namesKey = namePlural == null ? `${name}s` : namePlural;
  const statusKey = `${name}Status`;
  function createState(curr, itemId, status = null, error = null) {
    if (curr == null) {
      return {
        [namesKey]: [],
        [statusKey]: {},
        lookup: {},
        status,
        error,
        count: 0,
        offset: 0,
        limit: 0,
        next: null
      };
    }
    if (itemId == null) {
      return {
        count: 0,
        offset: 0,
        limit: 0,
        next: null,
        [namesKey]: [],
        [statusKey]: {},
        ...curr,
        lookup: {
          ...curr?.lookup
        },
        status,
        error
      };
    }
    const rtn = {
      ...curr,
      [statusKey]: {
        ...curr[statusKey]
      },
      lookup: {
        ...curr.lookup
      }
    };
    if (status == null) {
      delete rtn[statusKey][itemId];
    } else {
      rtn[statusKey][itemId] = {
        status,
        error
      };
    }
    return rtn;
  }

  // eslint-disable-next-line default-param-last
  function groupedList(state = initialState, action) {
    if (typeof actionHandlers === 'function') {
      const nState = actionHandlers(state, action, createState);
      if (nState !== state) {
        return nState;
      }
    }
    switch (action.type) {
      case STATUS:
        {
          const {
            [groupKey]: resourceId,
            [idActionKey]: itemId,
            status,
            error = null
          } = action;
          const nState = {
            ...state
          };
          nState[resourceId] = createState(state[resourceId], itemId, status, error);
          return nState;
        }
      case LIST:
        {
          const {
            requestId,
            // used to determine if this is a new/same/refresh request
            [groupKey]: resourceId,
            [itemsKey]: items,
            [countKey]: count = 0,
            [nextKey]: next = null,
            [limitKey]: limit = 0,
            [offsetKey]: offset = 0
          } = action;
          const nState = {
            ...state
          };
          nState[resourceId] = createState(state[resourceId]);
          nState[resourceId].count = count;
          nState[resourceId].next = next;
          nState[resourceId].offset = offset;
          nState[resourceId].limit = limit;
          if (nState[resourceId].requestId !== requestId) {
            nState[resourceId][namesKey] = [];
            nState[resourceId].requestId = requestId;
          }
          nState[resourceId][namesKey] = [...nState[resourceId][namesKey]];
          items.forEach((item, index) => {
            const itemId = item[idKey];
            const nItem = merge(item, nState[resourceId].lookup[itemId]);
            nState[resourceId].lookup[itemId] = nItem;
            nState[resourceId][namesKey][index + offset] = nItem;
          });
          return nState;
        }
      case ADD:
        {
          const {
            [groupKey]: resourceId,
            [itemKey]: item
          } = action;
          const itemId = item[idKey];
          const nState = {
            ...state
          };
          nState[resourceId] = createState(state[resourceId], item[idKey]);
          nState[resourceId].lookup[itemId] = merge(item, nState[resourceId].lookup[itemId]);
          const sharedItem = nState[resourceId].lookup[itemId];
          // TODO : this is incorrect as item might not be the next element in the list!
          const idx = nState[resourceId][namesKey].findIndex(i => i.id === itemId);
          if (idx < 0) {
            nState[resourceId][namesKey] = [...nState[resourceId][namesKey], sharedItem];
            nState[resourceId].count++;
          } else {
            nState[resourceId][namesKey] = [...nState[resourceId][namesKey]];
            nState[resourceId][namesKey][idx] = sharedItem;
          }
          return nState;
        }
      case REMOVE:
        {
          const {
            [groupKey]: resourceId,
            [idActionKey]: itemId
          } = action;
          const nState = {
            ...state
          };
          nState[resourceId] = createState(state[resourceId], itemId);
          const hasLookup = nState[resourceId].lookup[itemId] != null;
          if (nState[resourceId][namesKey].find(u => u[idKey] === itemId) !== undefined) {
            // eslint-disable-next-line eqeqeq
            nState[resourceId][namesKey] = nState[resourceId][namesKey].filter(u => u[idKey] != itemId);
            nState[resourceId].count--;
          } else if (!hasLookup) {
            // not change, return state
            return state;
          }
          delete nState[resourceId].lookup[itemId];
          return nState;
        }
      case BULK_REMOVE:
        {
          const {
            [groupKey]: resourceId,
            [idActionKey]: itemId
          } = action;
          const arr = Array.isArray(itemId) ? itemId : [itemId];
          const cs = state[resourceId] || createState(null);
          const ns = {
            ...cs,
            [namesKey]: [...(cs[namesKey] || [])],
            [statusKey]: {
              ...cs[statusKey]
            },
            lookup: {
              ...cs.lookup
            }
          };
          let hasChange = false;
          for (let n = 0; n < arr.length; n++) {
            const id = arr[n];
            hasChange = hasChange || ns.lookup[id] != null;
            delete ns[statusKey][id];
            delete ns.lookup[id];
            const idx = ns[namesKey].findIndex(u => u[idKey] === id);
            if (idx >= 0) {
              ns[namesKey].splice(idx, 1);
              ns.count--;
              hasChange = true;
            }
          }
          if (hasChange) {
            const nState = {
              ...state
            };
            nState[resourceId] = ns;
            return nState;
          }
          return state;
        }
      case UPDATE:
        {
          const {
            [groupKey]: resourceId,
            [itemKey]: item
          } = action;
          const itemId = item[idKey];
          const nState = {
            ...state
          };
          nState[resourceId] = createState(state[resourceId], item[idKey]);
          nState[resourceId].lookup[itemId] = merge(item, nState[resourceId].lookup[itemId]);
          nState[resourceId][namesKey] = nState[resourceId][namesKey].map(itm => {
            // eslint-disable-next-line eqeqeq
            if (itm[idKey] != itemId) {
              return itm;
            }
            return nState[resourceId].lookup[itemId];
          });
          return nState;
        }
      default:
        {
          return state;
        }
    }
  }
  function getStatus(state, resourceId, itemId) {
    if (state && state[resourceId]) {
      if (itemId != null) {
        const rs = state[resourceId][statusKey];
        return rs && rs[itemId] && rs[itemId].status ? rs[itemId].status : null;
      }
      return state[resourceId].status;
    }
    return null;
  }
  function getError(state, resourceId, itemId) {
    if (state && state[resourceId]) {
      if (itemId != null) {
        const rs = state[resourceId][statusKey];
        return rs && rs[itemId] && rs[itemId].error ? rs[itemId].error : null;
      }
      return state[resourceId].error;
    }
    return null;
  }
  function getItems(state, resourceId) {
    if (state && state[resourceId] && state[resourceId][namesKey]) {
      return state[resourceId][namesKey];
    }
    return [];
  }
  function getItem(state, resourceId, itemId) {
    return state && state[resourceId] && state[resourceId].lookup[itemId] || null;
  }
  function get(state, resourceId) {
    return state && state[resourceId] || null;
  }
  function getCount(state, resourceId) {
    if (state && state[resourceId]) {
      return state[resourceId].count || 0;
    }
    return 0;
  }
  function getOffset(state, resourceId) {
    if (state && state[resourceId]) {
      return state[resourceId].offset || 0;
    }
    return 0;
  }
  function getLimit(state, resourceId) {
    if (state && state[resourceId]) {
      return state[resourceId].limit;
    }
    return -1;
  }
  function getNext(state, resourceId) {
    if (state && state[resourceId]) {
      return state[resourceId].next || null;
    }
    return null;
  }

  /**
   * Returns true if the requestId is not the current requestId or if 
   * the item is undefined
   */
  function needsLoading(resourceState, requestId, index) {
    if (resourceState && resourceState.requestId === requestId) {
      return resourceState[namesKey][index] === undefined;
    }
    return true;
  }

  // groupedList.createState = createState
  groupedList.getStatus = getStatus;
  groupedList.getError = getError;
  groupedList.get = get;
  groupedList.needsLoading = needsLoading;
  const cNames = `${namesKey[0].toUpperCase()}${namesKey.substring(1)}`;
  const cName = `${name[0].toUpperCase()}${name.substring(1)}`;
  groupedList[`get${cNames}`] = getItems;
  groupedList[`get${cName}`] = getItem;
  groupedList[`get${cName}Count`] = getCount;
  groupedList[`get${cName}Offset`] = getOffset;
  groupedList[`get${cName}Limit`] = getLimit;
  groupedList[`get${cName}Next`] = getNext;
  groupedList.createSelectors = (key, createSetStateKey = false) => {
    return {
      ...createSelectors(key, {
        get,
        getStatus,
        getError,
        [`get${cName}`]: getItem,
        [`get${cNames}`]: getItems,
        [`get${cName}Count`]: getCount,
        [`get${cName}Offset`]: getOffset,
        [`get${cName}Limit`]: getLimit,
        [`get${cName}Next`]: getNext
      }, createSetStateKey),
      needsLoading
    };
  };

  // Set up initial defaults to make methods available immediately with default state key
  const selectors = groupedList.createSelectors(defaultStateKey, true);
  Object.keys(selectors).forEach(key => {
    groupedList[key] = selectors[key];
  });
  return groupedList;
}
export default {
  create
};